// components/ChatMessages.tsx
import type React from 'react';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';

import { PhoneIcon } from 'ui';

import classes from '../Chat.module.scss';
import screen from '../ChatScreen.module.scss';
import type { ChatMessage, ContactSharePayload } from '../types';
import { ChatDateSeparator } from './ChatDateSeparator';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';

interface ChatMessagesProps {
  messages: ChatMessage[];
  currentUserId: number;
  isLoading?: boolean;
}

const getDateKey = (message: ChatMessage): string => {
  const raw = message.created_at || message.timestamp;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return raw;
  }
  return date.toISOString().slice(0, 10);
};

const parseContactShare = (text: string): ContactSharePayload | null => {
  try {
    const parsed = JSON.parse(text) as ContactSharePayload;
    if (parsed?.kind === 'contact_share') {
      return parsed;
    }
  } catch {
    return null;
  }
  return null;
};

const isProposal = (types?: string[]) => Boolean(types?.includes('proposal'));

const proposalPrice = (types?: string[]): number | null => {
  const raw = types?.find((t) => typeof t === 'string' && t.startsWith('price:'));
  if (!raw) return null;
  const value = Number(raw.slice('price:'.length));
  return Number.isFinite(value) && value > 0 ? value : null;
};

const STATUS_TITLES: Record<ChatMessage['status'], string> = {
  sending: 'Отправляется',
  sent: 'Отправлено',
  delivered: 'Доставлено',
  read: 'Прочитано',
  failed: 'Не удалось отправить',
};

/** Статус своего сообщения: часы - отправляется, одна галочка - отправлено,
 *  две серые - доставлено, две синие - прочитано. */
export const MessageStatusIcon: React.FC<{ status: ChatMessage['status'] }> = ({ status }) => {
  const title = STATUS_TITLES[status] || '';
  const cls = `${screen.msgStatus} ${status === 'read' ? screen.msgStatusRead : ''} ${
    status === 'failed' ? screen.msgStatusFailed : ''
  }`;
  return (
    <span className={cls} title={title} aria-label={title} role='img' data-status={status}>
      {status === 'sending' ? (
        <svg viewBox='0 0 16 16' width='14' height='14' fill='none' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round'>
          <circle cx='8' cy='8' r='6' />
          <path d='M8 4.8V8l2.2 1.4' />
        </svg>
      ) : status === 'failed' ? (
        <svg viewBox='0 0 16 16' width='14' height='14' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round'>
          <circle cx='8' cy='8' r='6.2' />
          <path d='M8 4.6v4.2' />
          <path d='M8 11.2v.2' />
        </svg>
      ) : status === 'sent' ? (
        <svg viewBox='0 0 16 16' width='16' height='14' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round'>
          <path d='M3 8.5l3.2 3L13 4.5' />
        </svg>
      ) : (
        <svg viewBox='0 0 20 16' width='20' height='14' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round'>
          <path d='M1.5 8.5l3.2 3L11.5 4.5' />
          <path d='M8.6 11.2l.3.3L15.7 4.5' />
        </svg>
      )}
    </span>
  );
};

const ContactShareMessage: React.FC<{ payload: ContactSharePayload }> = ({ payload }) => {
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const handleCopy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      setTimeout(() => setCopiedText(null), 1500);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedText(text);
      setTimeout(() => setCopiedText(null), 1500);
    }
  }, []);

  return (
    <div className={classes['contact-share-message']}>
      <p className={classes['contact-share-title']}>
        {payload.text || 'Контакты переданы по взаимному согласию'}
      </p>
      <div className={classes['contact-share-phones']}>
        {(payload.phones ?? []).map((entry) => {
          const telHref = `tel:${String(entry.phone).replace(/[^\d+]/g, '')}`;
          return (
            <a
              key={`${entry.role}-${entry.phone}`}
              href={telHref}
              className={`${classes['contact-share-phone']} ${
                classes[`contact-share-phone-${entry.role}`] || ''
              }`}
              onClick={() => {
                void handleCopy(entry.phone);
              }}
              title='Позвонить или скопировать номер'
            >
              <span className={classes['contact-share-call-icon']} aria-hidden='true'>
                <PhoneIcon />
              </span>
              <span className={classes['contact-share-text']}>
                <strong>{entry.role_label}:</strong> {entry.name}
                <br />
                {entry.phone}
              </span>
              {copiedText === entry.phone && (
                <span className={classes['copy-toast']}> Скопировано</span>
              )}
            </a>
          );
        })}
        {(payload.emails ?? []).map((entry) => (
          <a
            key={`${entry.role}-${entry.email}`}
            href={`mailto:${entry.email}`}
            className={`${classes['contact-share-phone']} ${
              classes[`contact-share-phone-${entry.role}`] || ''
            }`}
            onClick={() => {
              void handleCopy(entry.email);
            }}
            title='Написать или скопировать почту'
          >
            <span className={classes['contact-share-call-icon']} aria-hidden='true'>
              <svg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'>
                <path d='M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z' />
              </svg>
            </span>
            <span className={classes['contact-share-text']}>
              <strong>{entry.role_label}:</strong> {entry.name}
              <br />
              {entry.email}
            </span>
            {copiedText === entry.email && (
              <span className={classes['copy-toast']}> Скопировано</span>
            )}
          </a>
        ))}
      </div>
    </div>
  );
};

export const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  currentUserId,
  isLoading = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const prevMessagesLengthRef = useRef(messages.length);

  const scrollToBottom = (smooth = false) => {
    if (!containerRef.current) return;
    containerRef.current.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: smooth ? 'smooth' : 'auto',
    });
    setShowScrollButton(false);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      setShowScrollButton(distanceFromBottom > 200);
    };

    container.addEventListener('scroll', handleScroll);
    const initTimer = setTimeout(handleScroll, 100);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(initTimer);
    };
  }, []);

  useEffect(() => {
    if (!isLoading && messages.length > 0) {
      const timer = setTimeout(() => scrollToBottom(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isLoading, messages.length]);

  useEffect(() => {
    if (messages.length > prevMessagesLengthRef.current) {
      const timer = setTimeout(() => scrollToBottom(true), 100);
      prevMessagesLengthRef.current = messages.length;
      return () => clearTimeout(timer);
    }
  }, [messages.length]);

  const groupedMessages = useMemo(() => {
    const groups: Array<{ dateKey: string; items: ChatMessage[] }> = [];

    messages.forEach((message) => {
      const dateKey = getDateKey(message);
      const lastGroup = groups[groups.length - 1];
      if (lastGroup?.dateKey === dateKey) {
        lastGroup.items.push(message);
      } else {
        groups.push({ dateKey, items: [message] });
      }
    });

    return groups;
  }, [messages]);

  if (isLoading) {
    return (
      <div className={`${classes['chat-messages']} ${screen.messages}`} ref={containerRef}>
        <div className={classes['loading-messages']}>
          <BrandLoader center label='Загрузка сообщений...' />
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return <div className={`${classes['chat-messages']} ${screen.messages}`} ref={containerRef} />;
  }

  return (
    <div className={`${classes['chat-messages']} ${screen.messages}`} ref={containerRef}>
      {groupedMessages.map((group) => (
        <div key={group.dateKey}>
          <ChatDateSeparator date={group.items[0].created_at || group.items[0].timestamp} />
          {group.items.map((message) => {
            const isOwn = message.sender_id === currentUserId;
            const isContactShare =
              message.message_types?.includes('contact_share') ||
              message.sender_role === 'system';
            const contactPayload = isContactShare ? parseContactShare(message.text) : null;
            const key = `${message.id}-${message.created_at}`;

            if (contactPayload) {
              return (
                <div key={key} className={`${classes.message} ${classes['message--system']}`}>
                  <div className={`${classes['message-content']} ${screen.bubble} ${screen.bubbleOther}`}>
                    <ContactShareMessage payload={contactPayload} />
                    <div className={classes['message-footer']}>
                      <span className={`${classes['message-time']} ${screen.time}`}>{message.timestamp}</span>
                    </div>
                  </div>
                </div>
              );
            }

            const proposal = isProposal(message.message_types);
            const price = proposal ? proposalPrice(message.message_types) : null;
            const meta = (
              <span className={screen.msgMeta}>
                <span className={screen.msgTime}>{message.timestamp}</span>
                {isOwn ? <MessageStatusIcon status={message.status} /> : null}
              </span>
            );

            return (
              <div
                key={key}
                className={`${screen.msgRow} ${isOwn ? screen.msgRowOwn : screen.msgRowOther}`}
              >
                <div
                  className={`${screen.msgBubble} ${isOwn ? screen.msgBubbleOwn : screen.msgBubbleOther} ${
                    proposal ? screen.msgBubbleProposal : ''
                  } ${message.status === 'failed' ? screen.msgBubbleFailed : ''}`}
                >
                  {proposal ? (
                    <div className={screen.proposalHead}>
                      <span className={screen.proposalIcon} aria-hidden='true'>
                        <svg viewBox='0 0 24 24' width='16' height='16' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                          <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
                          <path d='M14 2v6h6' />
                          <path d='M9 15l2 2 4-4' />
                        </svg>
                      </span>
                      <span className={screen.proposalTitle}>Отклик репетитора</span>
                      {price ? (
                        <span className={screen.proposalPrice}>
                          {new Intl.NumberFormat('ru-RU').format(price)} руб. / час
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  {message.text ? <p className={screen.msgText}>{message.text}</p> : null}
                  {meta}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {showScrollButton && (
        <button
          className={classes['scroll-to-bottom-button']}
          onClick={() => scrollToBottom(true)}
          title='Прокрутить вниз'
          type='button'
        >
          <svg viewBox='0 0 24 24' width='20' height='20' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
            <path d='M6 9l6 6 6-6' />
          </svg>
        </button>
      )}
    </div>
  );
};